import { faTrash } from '@fortawesome/free-solid-svg-icons';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { Button, ButtonGroup, TextField } from '@mui/material';
import { differenceInCalendarDays, isSameDay } from 'date-fns';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import useAppointment from '../../../../../hooks/useAppointment';
import { Appointment, EventInterface, Meetingroom } from '../../../../../interfaces/interfaces';
import VyAnimatedFontAwesomeIconButton from '../../../../common/VyAnimatedFontAwesomeIconButton';
import VyTooltip from '../../../../common/VyTooltip';
import AppointmentSlot from './AppointmentSlot';
import AppointmentValidationModal from './AppointmentValidationModal';

const AppointmentBoard = ({
  date,
  endDate,
  refresh,
  meetingrooms,
  bookingmode = false,
  event,
  showDatePicker = true,
  zoomProp,
  showDeleteUnbooked = false,
  boothId,
}: {
  date: string;
  endDate: string;
  meetingrooms?: Meetingroom[];
  bookingmode?: boolean;
  event?: EventInterface;
  refresh?: Function;
  toastManager?: any;
  showDatePicker?: boolean;
  zoomProp?: number;
  showDeleteUnbooked?: boolean;
  boothId?: string;
}) => {
  const [currentAppointment, setCurrentAppointment] = useState<Appointment | undefined>(undefined);
  const [currentDate, setCurrentDate] = useState<string>(date || '');
  const [zoom, setZoom] = useState<number>(zoomProp || 1);

  const { t } = useTranslation(['appointment', 'eventhome']);
  const { defaultDate, deleteAll, findAllUnbookedAppointments } = useAppointment();

  const startTime = event?.modules.booking.settings.start_time || 8;
  const endTime = event?.modules.booking.settings.end_time || 18;

  useEffect(() => {
    if (date && !showDatePicker) setCurrentDate(date);
    else {
      const defaultD = defaultDate(date, endDate);
      defaultD && setCurrentDate(defaultD);
    }
  }, [date]);

  useEffect(() => {
    if (zoomProp) setZoom(zoomProp);
  }, [zoomProp]);

  async function deleteAllEmpty(meetingId: string) {
    const appointments = findAllUnbookedAppointments(meetingId, meetingrooms);
    await deleteAll(appointments);
    refresh && refresh();
  }

  const displaySlots = () => {
    if (meetingrooms) {
      let start = startTime;
      let end = endTime;
      if (endTime - startTime < 1) {
        start = 8;
        end = 20;
      }
      let slotWidth = `${100 / (end - start + 1)}%`;
      if (zoom > 1) slotWidth = `${(zoom * 50) / (end - start + 1)}cqw`;
      const slots = [];
      const height = `${Object.keys(meetingrooms).length * 3}em`;
      for (let i: number = start; i <= end; i += 1) {
        slots.push(
          <div
            key={i}
            style={{
              width: slotWidth,
              display: 'flex',
              flexDirection: 'column',
            }}>
            <div
              className="cell h-[4em] relative border-b-2"
              style={{
                position: 'relative',
                borderBottom: '2px solid #CCCCCC',
                height,
              }}>
              <div
                style={{
                  position: 'absolute',
                  width: '1px',
                  height: '100%',
                  background: '#F2F2F2',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  left: 0,
                  right: 0,
                  textAlign: 'center',
                }}
              />
              {i !== endTime && (
                <div
                  style={{
                    position: 'absolute',
                    right: 0,
                    width: '1px',
                    height: '100%',
                    background: '#F2F2F2',
                  }}
                />
              )}
            </div>
            <span
              style={{
                position: 'relative',
                marginLeft: 'auto',
                marginRight: 'auto',
                left: 0,
                right: 0,
                textAlign: 'center',
                bottom: 0,
              }}>
              {i}:00
            </span>
          </div>
        );
      }
      return slots;
    }
  };

  return (
    <div className="flex flex-col w-full overflow-auto">
      {showDatePicker && differenceInCalendarDays(new Date(endDate), new Date(date)) > 0 && (
        <TextField
          id="date"
          label={t('appointment:appointment-day')}
          type="date"
          className="mt-3"
          defaultValue={defaultDate(date, endDate)}
          variant="outlined"
          color="secondary"
          required
          inputProps={{
            min: date?.slice(0, 10),
            max: endDate?.slice(0, 10),
            style: { width: '100%' },
          }}
          onChange={(e) => setCurrentDate(e.target.value)}
        />
      )}

      <div className="flex justify-end w-full mt-2">
        {bookingmode && !zoomProp && (
          <ButtonGroup size="small" aria-label="small outlined button group">
            <Button
              disabled={zoom <= 1}
              onClick={() => {
                setZoom((zoom) => (zoom -= 1));
              }}>
              <ZoomOutIcon />
            </Button>
            <Button disabled>x {zoom}</Button>
            <Button
              disabled={zoom >= 3}
              onClick={() => {
                setZoom((zoom) => (zoom += 1));
              }}>
              <ZoomInIcon />
            </Button>
          </ButtonGroup>
        )}
      </div>
      <div style={{ marginBottom: '2em' }} />
      <div className="w-full overflow-auto">
        <div className="min-w-[700px] relative flex flex-1">
          <div className="flex flex-col relative min-w-[6em] w-[8cqw] max-w-[25em]">
            {meetingrooms &&
              meetingrooms.map((room, i) => (
                <div
                  className="w-full truncate absolute whitespace-nowrap"
                  key={'appointment-room-' + i}
                  style={{
                    bottom: `${i * 3 + 2}em`,
                  }}>
                  {showDeleteUnbooked && findAllUnbookedAppointments(room._id, meetingrooms).length > 0 && (
                    <VyAnimatedFontAwesomeIconButton
                      color="primary"
                      tooltip={t('appointment:delete-all-empty') || ''}
                      icon={faTrash}
                      style={{ marginRight: '1rem' }}
                      hideIconAfterPromise
                      onClick={() => deleteAllEmpty(room._id)}
                    />
                  )}
                  <VyTooltip title={room?.contactperson}>
                    <span>
                      {room.contactperson ?? (
                        <>
                          {t('account:columns-meetingroom')} {i}
                        </>
                      )}
                    </span>
                  </VyTooltip>
                </div>
              ))}
          </div>
          <div className="flex flex-1 relative">
            {displaySlots()}
            {meetingrooms &&
              currentDate &&
              meetingrooms.map((room, i) => {
                {
                  return room?.meetingappointments
                    ?.filter((appointment) => isSameDay(new Date(Date.parse(appointment.datetime_start)), new Date(currentDate)))
                    ?.filter((appointment) => new Date(appointment.datetime_start).getHours() >= startTime)
                    .map((appointment: Appointment, index) => (
                      <AppointmentSlot
                        key={appointment._id}
                        appointment={appointment}
                        startTime={startTime}
                        endTime={endTime}
                        onClick={() => setCurrentAppointment(appointment)}
                        slotIndex={i}
                        bookingmode={bookingmode}
                        boothId={boothId}
                      />
                    ));
                }
              })}
          </div>
        </div>
      </div>

      {currentAppointment && (
        <AppointmentValidationModal
          appointment={currentAppointment}
          onClose={() => setCurrentAppointment(undefined)}
          bookingmode={bookingmode}
          boothId={boothId}
        />
      )}
    </div>
  );
};

export default AppointmentBoard;
